import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import { useMutation } from '@tanstack/react-query'
import { apiClient, formatBytes, useCurrentUser } from '@dogdisk/shared'
import { MobileScreen } from '../components/mobile-screen'
import { MediaArea } from '../components/file-view/media-area'
import { type FileViewKind } from '../components/file-view/details-card'
import { streamUrlOf } from '../components/file-view/use-file-meta'
import { resourceUrl } from '../_lib/resource-node-pref'
import { useResolvePublicFiles } from '../_lib/article-api'

// 预览页 /preview/:fileId(别人的公开文件)— .pen 帧:
//   image → q5D4P / video → AGXin / audio → Qk72B / 其他 → AFxaz
// 布局参考 /file/:fileId(file-preview.tsx):HJWZI header(返回 + 标题 + ⋮ 占位)
//   + 媒体区(grow=false)+ 信息卡 + 弹性占位 + sticky ActionBar。
// 2026-06-04(参考 .pen q5D4P/AGXin/Qk72B/AFxaz + decided F-U D39(h)/X5、file_storage R-07):
//   底部 ActionBar 两按钮「转存」(POST /gapi/user/files/{id}/save-to-pan,VIP-only)+「下载」。
//   非 owner 媒体细节(分辨率/时长)不可见 → 信息卡只展示通用字段(类型/大小/时间/上传者)。
// 🔀 Mixed:匿名可看;转存/下载触发登录(按钮级守卫,带 next 回跳)。
// 数据源走 security-definer RPC get_public_files([id])(file_info 不可列出)。

function frameIdOf(kind: FileViewKind): string {
  if (kind === 'image') return 'q5D4P'
  if (kind === 'video') return 'AGXin'
  if (kind === 'audio') return 'Qk72B'
  return 'AFxaz'
}

// mime → 友好类型标签(.pen InfoSection「文件类型」:PNG 图片 / MP4 视频 / MP3 音频 / PDF 文档)
function friendlyType(mime: string): string {
  if (!mime) return '文件'
  const [main, sub = ''] = mime.split('/')
  const ext = sub.toUpperCase()
  if (main === 'image') return ext ? `${ext} 图片` : '图片'
  if (main === 'video') return ext ? `${ext} 视频` : '视频'
  if (main === 'audio') return ext ? `${ext} 音频` : '音频'
  if (mime === 'application/pdf') return 'PDF 文档'
  return ext ? `${ext} 文件` : '文件'
}

export function PreviewPage() {
  const { fileId } = useParams<{ fileId: string }>()
  const navigate = useNavigate()
  const { status } = useCurrentUser()
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [saveNotice, setSaveNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  // 未登录点转存/下载 → 跳 login(按钮级守卫;带 next 回跳本预览页)。
  // 契约:docs/ui/user-page-flows/media-msg-ticket.md「无 session 时跳登录」。
  const requireLogin = (): boolean => {
    if (status === 'authenticated') return false
    void navigate(`/auth/login?next=${encodeURIComponent(`#/preview/${fileId}`)}`)
    return true
  }

  const filesQ = useResolvePublicFiles(fileId ? [fileId] : [])
  const meta = filesQ.data?.[0] ?? null

  const mime = meta?.mime_type ?? ''
  const isImage = mime.startsWith('image/')
  const isVideo = mime.startsWith('video/')
  const isAudio = mime.startsWith('audio/')
  const streamURL = streamUrlOf(meta?.base_file_id ?? null, meta?.name ?? null)

  const kind: FileViewKind = isImage ? 'image' : isVideo ? 'video' : isAudio ? 'audio' : 'other'
  const frameId = frameIdOf(kind)

  // 转存到我的网盘(VIP-only;后端 errcode.VIPRequired 拒普通用户 → 降级提示去开通)
  const saveMut = useMutation({
    mutationFn: async () => {
      if (!fileId) throw new Error('no fileId')
      return apiClient.post<{ file_info_id: string; base_file_id: string }>(
        `/gapi/user/files/${fileId}/save-to-pan`,
        {},
      )
    },
    onSuccess: () => setSaveNotice({ kind: 'ok', text: '已转存到我的网盘' }),
    onError: (e) => {
      const msg = e instanceof Error ? e.message : '转存失败'
      const isVip = /VIP|会员|vip/i.test(msg)
      setSaveNotice({ kind: 'error', text: isVip ? '转存为会员专属功能,请先开通会员' : msg })
    },
  })

  const downloadMut = useMutation({
    mutationFn: async () => {
      if (!fileId) throw new Error('no fileId')
      return apiClient.get<{ url: string; expires_at: string }>(`/gapi/user/download/${fileId}`)
    },
    onSuccess: (resp) => {
      if (!resp.url) {
        setDownloadError('下载链接为空')
        return
      }
      window.location.href = resourceUrl(resp.url)
    },
    onError: (e) => setDownloadError(e instanceof Error ? e.message : '下载失败'),
  })

  return (
    <MobileScreen bg="white">
      {/* Header(HJWZI):44×44 surface 圆角方钮 + 22px 居中标题 + ⋮ 占位(非 owner 暂无动作) */}
      <header
        data-testid={frameId}
        className="flex items-center justify-between gap-3 px-6 pt-[max(10px,env(safe-area-inset-top))] pb-3"
      >
        <BackButton className="shrink-0" />
        <h1 className="flex-1 truncate text-center text-[22px] font-semibold text-[#1A1A1A]">{meta?.name ?? '预览'}</h1>
        <button
          type="button"
          data-testid={`${frameId}-more-btn`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F6F7F8] text-[#1A1A1A] active:bg-[#EDEEF0]"
          aria-label="更多"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
        </button>
      </header>

      {/* grow=false:媒体区不撑满视口,内容自上而下;信息卡贴近媒体(mt-[34px]) */}
      <MediaArea
        grow={false}
        frameId={frameId}
        isImage={isImage}
        isVideo={isVideo}
        isAudio={isAudio}
        isLoading={filesQ.isLoading}
        streamURL={streamURL}
        coverUrl={null}
        name={meta?.name}
        mime={mime}
      />

      {!filesQ.isLoading && !meta && (
        <p className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">文件不存在或非公开</p>
      )}

      {/* 通用信息卡(无 CoverRow / 无媒体细节 — 非 owner);.pen InfoSection 4 行 */}
      {meta && (
        <div className="mt-[34px] px-5 pb-2">
          <section data-testid={`${frameId}-info`} className="flex flex-col gap-3 rounded-2xl bg-[#F6F7F8] p-4">
            <p className="sr-only">文件信息</p>
            <InfoRow label="文件类型" value={friendlyType(mime)} />
            <InfoRow label="文件大小" value={meta.size_original != null ? formatBytes(meta.size_original) : '—'} />
            <InfoRow label="上传时间" value={meta.created_at?.slice(0, 10) ?? '—'} />
            <InfoRow label="上传者" value={meta.owner_nickname ? `@${meta.owner_nickname}` : '—'} />
          </section>
        </div>
      )}

      {/* 弹性占位:内容不足时把 ActionBar 压到屏幕底部 */}
      <div className="flex-1" />

      {/* ActionBar(DYFaW):sticky bottom;转存(实心 primary)+ 下载(描边 primary) */}
      <div
        className="sticky bottom-0 z-10 bg-transparent px-5 pt-2"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
      >
        {saveNotice && (
          <p className={`mb-2 text-center text-[12px] ${saveNotice.kind === 'ok' ? 'text-[#19caad]' : 'text-[#FF6B6B]'}`}>
            {saveNotice.text}
          </p>
        )}
        {downloadError && <p className="mb-2 text-center text-[12px] text-[#FF6B6B]">{downloadError}</p>}
        <div className="flex gap-3">
          <button
            type="button"
            data-testid={`${frameId}-save-btn`}
            onClick={() => {
              if (requireLogin()) return
              setSaveNotice(null)
              saveMut.mutate()
            }}
            disabled={saveMut.isPending || !meta}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#19caad] text-[15px] font-semibold text-white disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v8" /><path d="m8 6 4 4 4-4" /><rect x="3" y="14" width="18" height="6" rx="2" /><path d="M7 17h.01" /></svg>
            转存
          </button>
          <button
            type="button"
            data-testid={`${frameId}-download-btn`}
            onClick={() => {
              if (requireLogin()) return
              setDownloadError(null)
              downloadMut.mutate()
            }}
            disabled={downloadMut.isPending || !meta}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border-[1.5px] border-[#19caad] bg-[#F6F7F8] text-[15px] font-semibold text-[#19caad] disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 4v12M5 11l7 7 7-7" /></svg>
            下载
          </button>
        </div>
      </div>
    </MobileScreen>
  )
}

// detailRow(.pen InfoSection):space-between,label·$text-secondary / value 500·$text-primary。
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="shrink-0 text-[17px] text-[#9CA3AF]">{label}</span>
      <span className="truncate text-right text-[17px] font-medium text-[#1A1A1A]">{value}</span>
    </div>
  )
}
